import { createContext, ReactNode, useContext, useMemo } from 'react';

import { ApiEndpoint } from '@/api/registry';
import { useSuspenseApiQuery } from '@/api/useApiQuery';
import type { TenantConfig } from '@/configs/tenants/types';
import { getRuntimeTenantAlias } from '@/runtimeConfig';
import { TenantResponse } from '@/services/controlPlane.service';
import { parseGrainsDictionary } from '@/utils/grains';

export interface ITenantContext {
  grains: Grains;
  grainsDictionary: GrainsDictionary;
  grainValues: string[];
  tenantAlias: string;
  tenantConfig: TenantConfig;
}

const defaultGrainsDictionary: GrainsDictionary = {};
const defaultGrains = parseGrainsDictionary(defaultGrainsDictionary);

export const defaultLowestGrains: Grains = defaultGrains.lowestGrains;

export const defaultTenantContext: ITenantContext = {
  grains: defaultLowestGrains,
  grainValues: defaultGrains.grainValues,
  grainsDictionary: defaultGrainsDictionary,
  tenantAlias: '',
  tenantConfig: {},
};

export const TenantContext = createContext<ITenantContext>(defaultTenantContext);

type Props = {
  children: ReactNode;
};

export function TenantProvider({ children }: Props) {
  const tenantAlias = getRuntimeTenantAlias();
  if (!tenantAlias) {
    throw new Error('Tenant alias missing from runtime-config.js');
  }

  const { data: tenant } = useSuspenseApiQuery<TenantResponse>(
    tenantAlias,
    ApiEndpoint.ControlPlaneTenantByAlias,
    { pathParams: { tenantAlias } },
    {
      gcTime: Infinity,
      staleTime: Infinity,
    },
  );

  if (!tenant.config) {
    throw new Error(`Tenant configuration missing for tenant alias: ${tenantAlias}`);
  }

  const tenantConfig = tenant.config;
  const grainsDictionary = (tenantConfig.grainsDictionary as GrainsDictionary) ?? defaultGrainsDictionary;

  const { lowestGrains, grainValues } = useMemo(() => parseGrainsDictionary(grainsDictionary), [grainsDictionary]);

  const providerValue: ITenantContext = {
    grains: lowestGrains,
    grainsDictionary,
    grainValues,
    tenantAlias,
    tenantConfig,
  };

  return <TenantContext.Provider value={providerValue}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  return useContext(TenantContext);
}
